// src/pages/analytics/payables/VendorScorecardTab.tsx
//
// Vendor scorecard (recipe vendor-scorecard, agent 3.24.0+): per vendor over
// POs dated in the range (PurHead/PurTran) — fill rate (received vs ordered),
// on-time rate (received date by the PO's required date + grace days), lead
// time, cost change over the range — plus spend. Open A/P is on the A/P Aging
// tab (payments are made in QuickBooks).

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Award, RefreshCw, Search } from 'lucide-react';
import { apiV2 as api } from '../../../services/apiV2';
import DateField from '../../../components/DateField';
import type { VendorScore } from '../../../types/payables';
import { exportCsv, type CsvColumns } from '../../../lib/csvExport';
import { exportXlsx } from '../../../lib/xlsxExport';
import {
  ChangePct, ExportButtons, LoadError, Spinner, Tile, controlsCls, fmtCur, labelCls,
  isoDaysAgo, tableWrap, tdCls, tdR, thCls, thR,
} from './apShared';

const COLS: CsvColumns<VendorScore> = [
  ['Code', r => r.code], ['Vendor', r => r.name], ['Spend', r => r.spend], ['Bills', r => r.bills],
  ['POs', r => r.pos], ['POs due', r => r.posDue], ['Fill rate %', r => r.fillRatePct ?? ''],
  ['On time %', r => r.onTimePct ?? ''], ['Not received', r => r.notReceived], ['Avg lead days', r => r.avgLeadDays ?? ''],
  ['Cost change %', r => r.costChangePct ?? ''],
];

function Rate({ pct, good = 95, ok = 85 }: { pct: number | null; good?: number; ok?: number }) {
  if (pct == null) return <span className="text-slate-600">–</span>;
  const cls = pct >= good ? 'text-emerald-400' : pct >= ok ? 'text-amber-300' : 'text-red-400';
  return <span className={`tabular-nums ${cls}`}>{pct.toFixed(1)}%</span>;
}

export function VendorScorecardTab() {
  const [dateFrom, setDateFrom] = useState(() => isoDaysAgo(365));
  const [dateTo, setDateTo] = useState(() => isoDaysAgo(0));
  const [graceDays, setGraceDays] = useState(0);
  const [search, setSearch] = useState('');

  const q = useQuery({
    queryKey: ['vendorScorecard', dateFrom, dateTo, graceDays],
    queryFn: () => api.getVendorScorecard({ dateFrom, dateTo, graceDays }),
    staleTime: 60_000,
    retry: 0,
  });
  const data = q.data;
  const vendors = useMemo(() => {
    const s = search.trim().toLowerCase();
    const rows = data?.vendors ?? [];
    return s ? rows.filter(v => v.name.toLowerCase().includes(s) || String(v.code).includes(s)) : rows;
  }, [data, search]);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2"><Award size={20} /> Vendor Scorecard</h2>
        <p className="text-sm text-slate-400 mt-0.5">
          How each vendor delivers on the POs placed in the range — fill rate and on-time count only POs already due
          {data && <span className="text-slate-500"> · {data.from} → {data.to}</span>}
        </p>
      </div>

      <div className={controlsCls}>
        <DateField label="POs from" value={dateFrom} onChange={setDateFrom} />
        <DateField label="To" value={dateTo} onChange={setDateTo} />
        <div className="flex flex-col gap-1">
          <span className={labelCls}>Grace days</span>
          <input type="number" min={0} max={60} value={graceDays}
            onChange={e => { const v = Number(e.target.value); if (Number.isFinite(v)) setGraceDays(Math.min(60, Math.max(0, Math.round(v)))); }}
            className="px-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white w-20 focus:outline-none focus:ring-1 focus:ring-blue-500/50" />
        </div>
        <div className="flex flex-col gap-1">
          <span className={labelCls}>Vendor</span>
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search vendor…"
              className="pl-6 pr-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white w-48 focus:outline-none focus:ring-1 focus:ring-blue-500/50" />
          </div>
        </div>
        <div className="flex-1" />
        <ExportButtons what="the scorecard" disabled={!vendors.length}
          onCsv={() => exportCsv('vendor-scorecard', COLS, vendors)} onXlsx={() => exportXlsx('vendor-scorecard', COLS, vendors)} />
        <button onClick={() => q.refetch()} disabled={q.isLoading}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
          <RefreshCw size={14} className={q.isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {q.error && <LoadError error={q.error} what="the vendor scorecard" />}

      {q.isLoading ? <Spinner /> : !q.error && data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Tile tone="violet" label="Spend" value={fmtCur(data.totals.spend)} sub={`${data.totals.pos} POs`} />
            <Tile label="Fill rate" value={<Rate pct={data.totals.fillRatePct} />} />
            <Tile label="On time" value={<Rate pct={data.totals.onTimePct} good={90} ok={75} />} />
            <Tile tone={data.totals.notReceived ? 'amber' : 'slate'} label="Due, not received" value={data.totals.notReceived} sub="POs past their required date" />
          </div>

          <div className={`${tableWrap} max-h-[620px]`}>
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-900">
                <tr>
                  <th className={thCls}>Vendor</th><th className={thR}>Spend</th><th className={thR}>POs</th>
                  <th className={thR}>Fill rate</th><th className={thR}>On time</th><th className={thR}>Not received</th>
                  <th className={thR}>Avg lead</th><th className={thR} title="First vs last PO cost per item in the range, weighted by spend">Cost change</th>
                </tr>
              </thead>
              <tbody>
                {vendors.map(v => (
                  <tr key={v.code} className="border-t border-slate-800/60">
                    <td className={tdCls}><span className="text-slate-500 mr-1.5">{v.code}</span>{v.name}</td>
                    <td className={`${tdR} font-medium`}>{fmtCur(v.spend)}</td>
                    <td className={tdR}>{v.pos}{v.posDue !== v.pos && <span className="text-slate-500"> ({v.posDue} due)</span>}</td>
                    <td className={tdR}><Rate pct={v.fillRatePct} /></td>
                    <td className={tdR}><Rate pct={v.onTimePct} good={90} ok={75} /></td>
                    <td className={`${tdR} ${v.notReceived > 0 ? 'text-amber-300' : 'text-slate-600'}`}>{v.notReceived || '–'}</td>
                    <td className={tdR}>{v.avgLeadDays != null ? `${v.avgLeadDays.toFixed(1)}d` : '–'}</td>
                    <td className={tdR}><ChangePct pct={v.costChangePct} invert /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
